import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../core/utils';
import { PaymentMethod, primaryCalculatorId, selectIsPaymentMethodCash, selectLeaseAnnualKmAllowance, selectPaymentFrequency, selectPaymentMethod, selectTermAndRate, selectWarrantyKey, } from '../../core/reducers/financialCalculators';
import { selectCurrentBodyType, selectFlattenedCurrentVehicleFromVehicles, selectIsCurrentVehicleElectric, } from '../../core/reducers/vehicles';
import { selectWarrantyOptionsModalKey, selectWarrantyOptionsModalType } from '../../core/reducers/ui';
import { WarrantyOptionTypes, WarrantyTypes } from '../../core/types';
import useFormatPriceWithCurrentLanguage from '../../utils/hooks/useFormatPriceWithCurrentLanguage';
import { useFetchCurrentVehiclePayment } from '../../core/apiHooks/useFetchPayment';
import useFetchWarranties from '../../core/apiHooks/useFetchWarranties';
const getOptionTypeFromWarranty = (warranty, checkedModalWarrantyCard, modalWarrantyType, isModal) => {
    if (!warranty) {
        return WarrantyOptionTypes.None;
    }
    if (warranty.bapType === WarrantyTypes.Extended ||
        (checkedModalWarrantyCard === WarrantyOptionTypes.Extended && warranty.typeName === WarrantyTypes.Comprehensive) ||
        (warranty.isRecommended && modalWarrantyType === WarrantyTypes.Extended && !isModal)) {
        return WarrantyOptionTypes.Extended;
    }
    if (warranty.bapType === WarrantyTypes.Flexible ||
        (warranty.isRecommended && checkedModalWarrantyCard === WarrantyTypes.Flexible) ||
        (warranty.isRecommended && modalWarrantyType === WarrantyTypes.Flexible && !isModal)) {
        return WarrantyOptionTypes.Flexible;
    }
    if (checkedModalWarrantyCard === WarrantyOptionTypes.Recommended ||
        (!checkedModalWarrantyCard && warranty.isRecommended) ||
        warranty.bapType === WarrantyTypes.Recommended) {
        return WarrantyOptionTypes.Recommended;
    }
    return WarrantyOptionTypes.None;
};
const useWarranties = (calculatorId = primaryCalculatorId) => {
    var _a, _b, _c, _d, _e;
    const { t } = useTranslation();
    const selectedWarrantyKey = useAppSelector(state => selectWarrantyKey(state, calculatorId));
    const selectedModalWarrantyKey = useAppSelector(state => selectWarrantyOptionsModalKey(state));
    const modalWarrantyType = useAppSelector(state => selectWarrantyOptionsModalType(state));
    const term = useAppSelector(state => selectTermAndRate(state, calculatorId));
    const annualKmAllowance = useAppSelector(state => selectLeaseAnnualKmAllowance(state, calculatorId));
    const paymentFrequency = useAppSelector(state => selectPaymentFrequency(state, calculatorId));
    const paymentMethod = useAppSelector(state => selectPaymentMethod(state, calculatorId));
    const isCashPaymentMethod = useAppSelector(state => selectIsPaymentMethodCash(state, calculatorId));
    const { transmissionDetIdentifier } = useAppSelector(selectFlattenedCurrentVehicleFromVehicles);
    const formatPriceWithCurrentLanguage = useFormatPriceWithCurrentLanguage();
    const bodyType = useAppSelector(selectCurrentBodyType);
    const isElectricVehicle = useAppSelector(selectIsCurrentVehicleElectric);
    const paymentApiResp = useFetchCurrentVehiclePayment(bodyType);
    const transmissionIds = transmissionDetIdentifier ? [transmissionDetIdentifier] : undefined;
    const warrantiesApiResp = useFetchWarranties(transmissionIds, bodyType, paymentMethod, term, paymentMethod === PaymentMethod.lease ? annualKmAllowance : undefined);
    const warrantiesWithPayment = (_a = warrantiesApiResp === null || warrantiesApiResp === void 0 ? void 0 : warrantiesApiResp.warranties) === null || _a === void 0 ? void 0 : _a.map(warranty => {
        var _a, _b;
        const warrantyPayment = (_b = (_a = paymentApiResp === null || paymentApiResp === void 0 ? void 0 : paymentApiResp.data) === null || _a === void 0 ? void 0 : _a.warranties) === null || _b === void 0 ? void 0 : _b.find(w => w.businessKey === warranty.businessKey);
        return {
            ...warranty,
            ...warrantyPayment,
        };
    });
    const isUninitialized = (paymentApiResp === null || paymentApiResp === void 0 ? void 0 : paymentApiResp.isUninitialized) || warrantiesApiResp.isUninitialized;
    const showLoader = isUninitialized || (paymentApiResp === null || paymentApiResp === void 0 ? void 0 : paymentApiResp.isFetching) || warrantiesApiResp.isFetching;
    const showError = !!(paymentApiResp === null || paymentApiResp === void 0 ? void 0 : paymentApiResp.error) || !!warrantiesApiResp.error;
    const allWarranties = warrantiesWithPayment === null || warrantiesWithPayment === void 0 ? void 0 : warrantiesWithPayment.map(warranty => ({
        ...warranty,
        yearsText: `${warranty === null || warranty === void 0 ? void 0 : warranty.yearsText}`,
        annualKmsText: `${warranty === null || warranty === void 0 ? void 0 : warranty.annualKms} km`,
        priceText: formatPriceWithCurrentLanguage(warranty === null || warranty === void 0 ? void 0 : warranty.priceTotal),
        paymentText: isCashPaymentMethod
            ? ''
            : `${formatPriceWithCurrentLanguage(warranty === null || warranty === void 0 ? void 0 : warranty.frequencyPayment)}/${t(`Shared.Common.${paymentFrequency}OptionLabel`)}`,
    }));
    const extendedWarranties = (_b = allWarranties === null || allWarranties === void 0 ? void 0 : allWarranties.filter(warranty => (warranty.bapType === WarrantyTypes.Extended && !warranty.isRecommended) ||
        ([WarrantyTypes.Comprehensive, WarrantyTypes.Global].includes(warranty.typeName) &&
            warranty.isRecommended))) === null || _b === void 0 ? void 0 : _b.sort((a, b) => (a.years && b.years ? a.years - b.years : 0)).map(warranty => {
        var _a;
        return ({
            ...warranty,
            description: t('Pages.Bap.extendedWarrantyText'),
            title: t('Pages.Bap.extendedWarrantyLabel'),
            titleTooltipText: t('Pages.Bap.extendedWarrantyTooltipText'),
            summary: `${t('Pages.Bap.extendedLabel')} | ${(_a = warranty === null || warranty === void 0 ? void 0 : warranty.name) === null || _a === void 0 ? void 0 : _a.replace('* ', '')}`,
        });
    });
    const flexibleWarranties = (_c = allWarranties === null || allWarranties === void 0 ? void 0 : allWarranties.filter(warranty => warranty.bapType === WarrantyTypes.Flexible ||
        (![WarrantyTypes.Comprehensive, WarrantyTypes.Global].includes(warranty.typeName) &&
            warranty.isRecommended))) === null || _c === void 0 ? void 0 : _c.sort((a, b) => (a.years && b.years ? a.years - b.years : 0)).map(warranty => {
        var _a;
        return ({
            ...warranty,
            description: t('Pages.Bap.flexibleWarrantyText'),
            title: t('Pages.Bap.flexibleWarrantyLabel'),
            titleTooltipText: warranty.isRecommended
                ? t('Pages.Bap.recommendedFlexTootltipText')
                : t('Pages.Bap.flexibleWarrantyTooltipText'),
            summary: `${t('Pages.Bap.flexibleLabel')} | ${(_a = warranty === null || warranty === void 0 ? void 0 : warranty.name) === null || _a === void 0 ? void 0 : _a.replace('* ', '')}`,
        });
    });
    const standardWarranties = allWarranties === null || allWarranties === void 0 ? void 0 : allWarranties.filter(warranty => warranty.bapType === WarrantyTypes.Standard).map(warranty => {
        var _a, _b, _c;
        return ({
            ...warranty,
            title: (_a = warranty === null || warranty === void 0 ? void 0 : warranty.type) === null || _a === void 0 ? void 0 : _a.name,
            titleTooltipText: isElectricVehicle
                ? t('Pages.Bap.standardWarrantyTooltipEvText')
                : t('Pages.Bap.standardWarrantyTooltipIceText'),
            includedText: t('Pages.Bap.includedLabel'),
            summary: ((_b = warranty === null || warranty === void 0 ? void 0 : warranty.name) === null || _b === void 0 ? void 0 : _b.replace('* ', '')) || ((_c = warranty === null || warranty === void 0 ? void 0 : warranty.label) === null || _c === void 0 ? void 0 : _c.replace('* ', '')),
        });
    });
    const recommendedWarranties = allWarranties === null || allWarranties === void 0 ? void 0 : allWarranties.filter(warranty => warranty.isRecommended).map(warranty => {
        var _a;
        const isExtendedWarranty = warranty.bapType === WarrantyTypes.Extended ||
            warranty.typeName === WarrantyTypes.Comprehensive ||
            warranty.typeName === WarrantyTypes.Global;
        return {
            ...warranty,
            title: t('Pages.Bap.recommendedLabel'),
            titleTooltipText: isExtendedWarranty
                ? t('Pages.Bap.recommendedExtTootltipText')
                : t('Pages.Bap.recommendedFlexTootltipText'),
            summary: `${isExtendedWarranty ? t('Pages.Bap.extendedLabel') : t('Pages.Bap.flexibleLabel')} | ${(_a = warranty === null || warranty === void 0 ? void 0 : warranty.name) === null || _a === void 0 ? void 0 : _a.replace('* ', '')}`,
            description: t('Pages.Bap.recommendedWarrantyText', {
                term: term === null || term === void 0 ? void 0 : term.toString(),
            }),
        };
    });
    const recommendedWarranty = recommendedWarranties === null || recommendedWarranties === void 0 ? void 0 : recommendedWarranties[0];
    const standardWarranty = standardWarranties === null || standardWarranties === void 0 ? void 0 : standardWarranties[0];
    const totalWarrantyCost = (_d = paymentApiResp === null || paymentApiResp === void 0 ? void 0 : paymentApiResp.data) === null || _d === void 0 ? void 0 : _d.warrantyAmount;
    const selectedWarranty = allWarranties === null || allWarranties === void 0 ? void 0 : allWarranties.find(warranty => warranty.businessKey === selectedWarrantyKey);
    const selectedModalWarranty = allWarranties === null || allWarranties === void 0 ? void 0 : allWarranties.find(warranty => warranty.businessKey === selectedModalWarrantyKey);
    const selectedOptionType = getOptionTypeFromWarranty(selectedWarranty);
    const selectedFlexibleWarrantyKey = selectedOptionType === WarrantyOptionTypes.Flexible ? selectedWarrantyKey : undefined;
    const selectedExtendedWarrantyAnnualKmsValue = selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.annualKms;
    const selectedExtendedWarrantyYearsValue = selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.years;
    const selectedModalExtendedWarrantyAnnualKmsValue = selectedModalWarranty === null || selectedModalWarranty === void 0 ? void 0 : selectedModalWarranty.annualKms;
    const selectedModalExtendedWarrantyYearsValue = selectedModalWarranty === null || selectedModalWarranty === void 0 ? void 0 : selectedModalWarranty.years;
    const uniqueYearOptions = [...new Set(extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.map(warranty => warranty.years))].sort((a, b) => a && b ? a - b : 0);
    const yearsWithAnnualKmOptions = uniqueYearOptions.map(y => {
        var _a;
        const uniqueAnnualKmsOptions = [
            ...new Set(extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.filter(warranty => warranty.years === y).map(warranty => warranty.annualKms)),
        ].sort((a, b) => (a && b ? a - b : 0));
        const yearsText = (_a = extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.find(warranty => warranty.years === y)) === null || _a === void 0 ? void 0 : _a.yearsText;
        return {
            years: y,
            annualKms: uniqueAnnualKmsOptions,
            yearsText,
        };
    });
    const selectedYearsValue = selectedExtendedWarrantyYearsValue || (uniqueYearOptions === null || uniqueYearOptions === void 0 ? void 0 : uniqueYearOptions[0]);
    const selectedYearsWithAnnualKms = yearsWithAnnualKmOptions.find(o => o.years === selectedYearsValue);
    const selectedAnnualKmsValue = (selectedYearsWithAnnualKms === null || selectedYearsWithAnnualKms === void 0 ? void 0 : selectedYearsWithAnnualKms.annualKms.includes(selectedExtendedWarrantyAnnualKmsValue))
        ? selectedExtendedWarrantyAnnualKmsValue
        : (_e = selectedYearsWithAnnualKms === null || selectedYearsWithAnnualKms === void 0 ? void 0 : selectedYearsWithAnnualKms.annualKms) === null || _e === void 0 ? void 0 : _e[0];
    const extendedWarranty = (extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.find(w => w.years === selectedModalExtendedWarrantyYearsValue &&
        w.annualKms === selectedModalExtendedWarrantyAnnualKmsValue)) ||
        (extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties.find(w => w.years === selectedYearsValue && w.annualKms === selectedAnnualKmsValue)) ||
        (extendedWarranties === null || extendedWarranties === void 0 ? void 0 : extendedWarranties[0]);
    const flexibleWarranty = (flexibleWarranties === null || flexibleWarranties === void 0 ? void 0 : flexibleWarranties.find(w => w.businessKey === selectedModalWarrantyKey)) ||
        (flexibleWarranties === null || flexibleWarranties === void 0 ? void 0 : flexibleWarranties.find(w => w.businessKey === selectedFlexibleWarrantyKey)) ||
        (flexibleWarranties === null || flexibleWarranties === void 0 ? void 0 : flexibleWarranties[0]);
    return {
        allWarranties,
        extendedWarranties,
        flexibleWarranties,
        standardWarranty,
        recommendedWarranty,
        extendedWarranty,
        flexibleWarranty,
        selectedYearsValue,
        selectedAnnualKmsValue,
        selectedWarrantyKey,
        selectedFlexibleWarrantyKey,
        selectedWarranty,
        yearsWithAnnualKmOptions,
        totalWarrantyCost,
        totalWarrantyCostText: formatPriceWithCurrentLanguage(totalWarrantyCost),
        showLoader,
        showError,
        getOptionTypeFromWarranty,
        isUninitialized,
        isCashPaymentMethod,
        isSelectedWarrantyRecommended: modalWarrantyType === WarrantyTypes.Recommended,
        selectedWarrantyType: modalWarrantyType,
    };
};
export default useWarranties;
